import { Component, OnInit, computed, input, model, signal } from '@angular/core';
import { takeUntil, tap } from 'rxjs/operators';
import type { LinkValue } from '@front-platform/content-builder';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { ModalsService } from '@core/services/modals/modals.service';
import { LinkNavigationService } from '@core/services/link-navigation/link-navigation.service';
import { PongoService } from '@core/services/pongo/pongo.service';
import { UserService } from '@core/services/user/user.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { resolvePhoneDefaultRegionCode } from '@common/utils/phone-default-region.util';
import { FidelityType } from '@shared/modals/kiosk-fidelity-registration/fidelity-type.enum';

import { PongoRegistrationStep } from './pongo-registration-step.enum';
import { isE164Phone } from './pongo-phone.util';

/** Pongo attach-customer: phone number is not a loyalty member. */
const PONGO_CUSTOMER_NOT_FOUND_CODE = 202;
/** Pongo create customer: phone number is already registered. */
const PONGO_ACCOUNT_ALREADY_EXISTS_CODE = 103;
/** Pongo create customer: phone number format is rejected. */
const PONGO_INVALID_PHONE_CODE = 100;

type PongoErrorType = 'account_not_found' | 'account_exists' | 'phone_format' | 'loyalty_error';

/** Content-builder links serialize booleans as the strings "true" and "false". */
function isKioskRegisterRequested(value: boolean | string | undefined | null): boolean {
  return value === true || value === 'true';
}

@Component({
  selector: 'app-pongo-registration-modal',
  templateUrl: './pongo-registration-modal.component.html',
  standalone: false,
})
export class PongoRegistrationModalComponent extends DestroyerBase implements OnInit {
  static handle = 'pongo-registration-modal';

  readonly phone = model('');
  readonly goToURLOnClose = input<string | LinkValue>();
  readonly goToKioskRegister = input<boolean | string | undefined>(false);
  readonly registrationStep = PongoRegistrationStep;
  readonly activeStep = model(this.registrationStep.PONGO_STEP_PHONE);
  readonly actionFontColor = input<IContentBuilderFieldColor | string>();
  readonly actionBackgroundColor = input<IContentBuilderFieldColor | string>();

  readonly isValidating = signal(false);
  readonly isRegistering = signal(false);
  readonly errorMessage = signal<string | undefined>(undefined);

  readonly defaultRegionCode = computed(() =>
    resolvePhoneDefaultRegionCode(
      undefined,
      this.marketplaceStoreSelector.selectMarketplaceCountrySignal(),
    ),
  );

  readonly actionFieldColor = computed<ContentBuilderFieldColors | undefined>(() => {
    const font = this.actionFontColor();
    const background = this.actionBackgroundColor();
    if (!font && !background) {
      return undefined;
    }
    return {
      font: this.getFormattedFontColor(font),
      background: this.getFormattedBackgroundColor(background),
    };
  });

  readonly FidelityType = FidelityType;

  constructor(
    private readonly modalsService: ModalsService,
    private readonly linkNavigation: LinkNavigationService,
    private readonly pongoService: PongoService,
    private readonly userService: UserService,
    private readonly analyticsService: AnalyticsService,
    private readonly marketplaceStoreSelector: MarketplaceStoreSelector,
  ) {
    super();
  }

  ngOnInit(): void {
    const openRegisterStep = isKioskRegisterRequested(this.goToKioskRegister());
    this.analyticsService.trackLoyaltyModalDisplay(
      'pongo',
      openRegisterStep ? 'kiosk_register' : 'kiosk_phone',
    );
    if (openRegisterStep) {
      this.activeStep.set(this.registrationStep.PONGO_STEP_KIOSK_REGISTER);
    }
  }

  onClose(): void {
    this.analyticsService.trackLoyaltyButtonClick('pongo', 'close_modal');
    this.finish();
  }

  onOrderWithoutAccount(): void {
    this.analyticsService.trackLoyaltyButtonClick('pongo', 'continue_without_loyalty');
    this.finish();
  }

  finish(): void {
    this.modalsService.close(PongoRegistrationModalComponent.handle);
    const goToURLOnClose = this.goToURLOnClose();
    if (!goToURLOnClose) {
      return;
    }
    this.linkNavigation.navigate(goToURLOnClose);
  }

  onGoToPhoneStep(): void {
    this.errorMessage.set(undefined);
    this.isRegistering.set(false);
    this.activeStep.set(this.registrationStep.PONGO_STEP_PHONE);
  }

  onAttach(): void {
    if (this.isValidating() || !isE164Phone(this.phone())) {
      return;
    }

    this.analyticsService.trackLoyaltyButtonClick('pongo', 'submit_phone');
    this.isValidating.set(true);
    this.errorMessage.set(undefined);

    this.pongoService
      .attachCustomer$(this.phone())
      .pipe(
        takeUntil(this._destroyerRef),
        tap(user => this.userService.setUser(user)),
      )
      .subscribe({
        next: () => {
          this.isValidating.set(false);
          this.activeStep.set(this.registrationStep.PONGO_STEP_KIOSK_CONFIRMATION);
        },
        error: error => {
          this.isValidating.set(false);
          this.handleAttachError(error);
        },
      });
  }

  onCreate(): void {
    this.createCustomer('submit_phone', 'pongo_kiosk_register');
  }

  onRegisterFromNotFound(): void {
    this.createCustomer('create_account', 'pongo_kiosk_account_not_found', true);
  }

  private createCustomer(
    action: 'submit_phone' | 'create_account',
    context: string,
    fromNotFound = false,
  ): void {
    const busy = fromNotFound ? this.isRegistering() : this.isValidating();
    if (busy || !isE164Phone(this.phone())) {
      return;
    }

    this.analyticsService.trackLoyaltyButtonClick('pongo', action);
    this.errorMessage.set(undefined);
    if (fromNotFound) {
      this.isRegistering.set(true);
    } else {
      this.isValidating.set(true);
    }

    this.pongoService
      .createCustomer$(this.phone(), false)
      .pipe(
        takeUntil(this._destroyerRef),
        tap(user => this.userService.setUser(user)),
      )
      .subscribe({
        next: () => {
          this.isValidating.set(false);
          this.isRegistering.set(false);
          this.activeStep.set(this.registrationStep.PONGO_STEP_KIOSK_REGISTER_CONFIRMATION);
        },
        error: error => {
          this.isValidating.set(false);
          this.isRegistering.set(false);
          this.handleCreateError(error, context);
        },
      });
  }

  private handleAttachError(error: unknown): void {
    const code = readPongoErrorCode(error);
    if (code === PONGO_CUSTOMER_NOT_FOUND_CODE) {
      this.trackPongoError(
        'account_not_found',
        code,
        'pongo.create-or-link-account.kiosk-account-not-found-step.title',
        'pongo_kiosk_phone_step',
      );
      this.activeStep.set(this.registrationStep.PONGO_STEP_KIOSK_ACCOUNT_NOT_FOUND);
      return;
    }

    const message = 'pongo.errors.generic-error';
    this.errorMessage.set(message);
    this.trackPongoError('loyalty_error', code, message, 'pongo_kiosk_phone_step');
  }

  private handleCreateError(error: unknown, context: string): void {
    const code = readPongoErrorCode(error);
    const message = createErrorMessage(code);
    this.errorMessage.set(message);
    this.trackPongoError(createErrorType(code), code, message, context);
  }

  private trackPongoError(
    errorType: PongoErrorType,
    code: number | undefined,
    message: string,
    context: string,
  ): void {
    this.analyticsService.trackErrorDisplay(
      errorType,
      code === undefined ? 'UNKNOWN' : String(code),
      message,
      context,
    );
  }

  private getFormattedFontColor(font: string | IContentBuilderFieldColor | undefined) {
    if (typeof font === 'string') {
      return font.startsWith('#')
        ? { type: ColorFieldTypesValues.Hex, value: font }
        : { type: ColorFieldTypesValues.Palette, value: font };
    }
    return font;
  }

  private getFormattedBackgroundColor(background: string | IContentBuilderFieldColor | undefined) {
    if (typeof background === 'string') {
      return background.startsWith('#')
        ? { type: ColorFieldTypesValues.Hex, value: background }
        : { type: ColorFieldTypesValues.Palette, value: background };
    }
    return background;
  }
}

function readPongoErrorCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object' || !('error' in error)) {
    return undefined;
  }
  const code = (error as { error?: { code?: unknown } }).error?.code;
  return typeof code === 'number' ? code : undefined;
}

function createErrorMessage(code: number | undefined): string {
  switch (code) {
    case PONGO_ACCOUNT_ALREADY_EXISTS_CODE:
      return 'pongo.create-account.errors.account-already-exists';
    case PONGO_INVALID_PHONE_CODE:
      return 'signup.invalid-phone';
    default:
      return 'pongo.errors.generic-error';
  }
}

function createErrorType(code: number | undefined): PongoErrorType {
  switch (code) {
    case PONGO_ACCOUNT_ALREADY_EXISTS_CODE:
      return 'account_exists';
    case PONGO_INVALID_PHONE_CODE:
      return 'phone_format';
    default:
      return 'loyalty_error';
  }
}
